import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton, Popover, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ITrainingSet } from '../../types/my-types';
import {
  TypographyPropertiesTitleStyled,
  DivVerticalDividerLarge,
} from '../common/StyledComponents';
import { StatsRow, StatsLabel, StatsValue } from '../common/StatsRow';

interface IProps {
  trainingSet: ITrainingSet;
}

const PopoverContent = styled('div')({
  padding: '16px',
  minWidth: '280px',
  maxWidth: '400px',
});

const PopoverTitle = styled(TypographyPropertiesTitleStyled)({
  marginBottom: '16px',
  fontSize: '11px',
});

const EmptyText = styled('span')({
  fontSize: '11px',
  color: '#999',
  fontStyle: 'italic',
  fontFamily: 'inherit',
});

interface StatsSectionProps {
  label: string;
  items: string[] | undefined;
  getLabel?: (item: string) => string;
}

const StatsSectionComponent: React.FC<StatsSectionProps> = ({
  label,
  items,
  getLabel,
}) => {
  return (
    <StatsRow>
      <StatsLabel>{label}</StatsLabel>
      {items && items.length > 0 ? (
        <StatsValue>
          {items.map((item, index) => (
            <span key={item}>
              {getLabel ? getLabel(item) : item}
              {index < items.length - 1 && ', '}
            </span>
          ))}
        </StatsValue>
      ) : (
        <EmptyText>None</EmptyText>
      )}
    </StatsRow>
  );
};

const TrainingSetStats: React.FC<IProps> = ({ trainingSet }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const {
    document_count,
    documents,
    fields_count,
    commodities,
    meters_per_commodity,
    marked_vendors,
    marked_tariffs,
  } = trainingSet;

  const documentCount = document_count ?? documents?.length;

  const hasAnyData =
    documentCount !== undefined ||
    fields_count !== undefined ||
    (commodities && commodities.length > 0) ||
    (marked_vendors && marked_vendors.length > 0) ||
    (marked_tariffs && marked_tariffs.length > 0);

  if (!hasAnyData) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const getCommodityLabel = (commodity: string): string => {
    const count = meters_per_commodity?.[commodity];
    return count !== undefined ? `${commodity}: ${count}` : commodity;
  };

  return (
    <>
      <Tooltip title="Training Set Details" placement="top">
        <IconButton
          onClick={handleClick}
          size="small"
          data-cy="training-set-stats-btn"
          sx={{
            color: open ? 'primary.main' : 'action.active',
            padding: '4px',
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <DivVerticalDividerLarge />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: { mt: 1 }
          }
        }}
      >
        <PopoverContent>
          <PopoverTitle>Training Set Details</PopoverTitle>

          {documentCount !== undefined && (
            <StatsRow>
              <StatsLabel>DOCUMENTS</StatsLabel>
              <StatsValue>{documentCount}</StatsValue>
            </StatsRow>
          )}

          {fields_count !== undefined && (
            <StatsRow>
              <StatsLabel>FIELDS</StatsLabel>
              <StatsValue>{fields_count}</StatsValue>
            </StatsRow>
          )}

          <StatsSectionComponent
            label="COMMODITIES"
            items={commodities}
            getLabel={getCommodityLabel}
          />

          <StatsSectionComponent
            label="MARKED VENDORS"
            items={marked_vendors}
          />

          <StatsSectionComponent
            label="MARKED TARIFFS"
            items={marked_tariffs}
          />
        </PopoverContent>
      </Popover>
    </>
  );
};

export default TrainingSetStats;
